import { registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeFrBe from '@angular/common/locales/fr-BE';
import localeNl from '@angular/common/locales/nl';
import localeNlBe from '@angular/common/locales/nl-BE';
import { APP_INITIALIZER, NgModule } from '@angular/core';

import { MetaReducer, StoreModule } from '@ngrx/store';
import { TranslateCompiler, TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { map } from 'rxjs/operators';

import { PortalZoneRestrictions } from '@common/services/api/helpers/portal-zone-restrictions.class';
import { PortalConfigService } from '@common/services/config/portal-config.service';
import AggregateMiddleware from '@common/services/middleware/aggregate.middleware';
import { PortalStoreRecordsStateService } from '@common/services/search-context/store-record-state.service';
import { rootReducer } from '@reducers/root.reducer';

import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { PortalApiInterceptor } from '@common/interceptors/auth-key.interceptor';
import { PublicAdvancedSearchSandboxService } from '@common/sandboxes/public-advanced-search-sandbox.service';
import { PortalPreviewDialogActionsService } from '@common/services/preview-dialog/preview-dialog-actions.service';
import { AgmCoreModule, LAZY_MAPS_API_CONFIG } from '@duxor/agm-core';
import { APP_NAME_TOKEN, AppName, CustomTranslateLoader, LocaleProvider, SharedLanguageService } from '@language';
import {
    BaseSandbox,
    createLocalStorageSyncReducer,
    GeoSandboxService,
    HttpService,
    LocalStoragePrefix,
    MhProfileConfigService,
    MHSearchRoutingService,
    PreviewDialogActionsService,
    RefreshShareTokenStrategy,
    RefreshTokenInterceptor,
    SearchService,
    SharedModule,
    ToastsModule,
    TokenInterceptor,
    TrackingModule
} from '@mediahaven/shared';
import { PublishedSandbox } from '@published/sandbox/published.sandbox';
import {
    AbstractGeoSandboxService,
    AbstractRecordsStateService,
    ApplicationConfigService,
    BaseAdvancedSearchSandbox,
    CACHE_CONFIG,
    ConfigService,
    CORE_APP_MODULES,
    EnvironmentConfigService,
    GoogleMapsConfig,
    PAGE_TITLE_CONFIG,
    RouterStateResolverService,
    ShAbstractRecordDownloadService,
    ShAbstractToastService,
    ShCacheInterceptor,
    ShCoreAppModule,
    ShDynamicDialogService,
    ShProfileConfigService,
    ShRecordDownloadService,
    ShRecordService,
    ShSearchRoutingService,
    StylingService,
    ZoneRestrictions
} from '@stylehaven/components';
import { TranslateMessageFormatCompiler } from 'ngx-translate-messageformat-compiler';
import { BootstrapRoutingModule } from './bootstrap-routing.module';
import { RootContainerComponent } from './containers/root-container.component';
import { BootstrapSandboxService } from './sandbox/bootstrap.sandbox.service';

registerLocaleData(localeNlBe);
registerLocaleData(localeNl);
registerLocaleData(localeFrBe);
registerLocaleData(localeDe);

const metaReducers: MetaReducer<any>[] = [
    createLocalStorageSyncReducer({
        localStoragePrefix: LocalStoragePrefix.Portal
    })
];
@NgModule({
    declarations: [RootContainerComponent],
    imports: [
        // Core App imports
        ...CORE_APP_MODULES,
        ShCoreAppModule.forRoot(HttpService),
        BootstrapRoutingModule,
        StoreModule.forRoot(rootReducer, { metaReducers }),

        // MH Shared imports
        SharedModule.forRoot(HttpService, AggregateMiddleware),

        // App specific imports
        AgmCoreModule.forRoot(),
        ToastsModule,
        TrackingModule.forRoot(),
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useClass: CustomTranslateLoader,
                deps: [APP_NAME_TOKEN]
            },
            compiler: {
                provide: TranslateCompiler,
                useClass: TranslateMessageFormatCompiler
            }
        })
    ],
    providers: [
        // #region Translations and Locales
        {
            provide: APP_NAME_TOKEN,
            useFactory: (): AppName => {
                return 'portal';
            }
        },
        {
            provide: APP_INITIALIZER,
            useFactory: (config: PortalConfigService, translateService: SharedLanguageService) => () => {
                return config
                    .getConfig()
                    .pipe(map(config => translateService.init(config.fallbackLanguage)))
                    .toPromise();
            },
            deps: [PortalConfigService, SharedLanguageService],
            multi: true
        },

        LocaleProvider,
        // #endregion
        // #region Config
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ShCacheInterceptor,
            multi: true
        },
        {
            provide: EnvironmentConfigService,
            useExisting: PortalConfigService
        },
        {
            provide: LAZY_MAPS_API_CONFIG,
            useClass: GoogleMapsConfig,
            deps: [PortalConfigService]
        },
        // #endregion
        // #region Styling
        StylingService,
        // #endregion
        // #region Services
        {
            provide: APP_INITIALIZER,
            useFactory: (config: RouterStateResolverService) => () => {
                return true;
            },
            deps: [RouterStateResolverService],
            multi: true
        },
        {
            provide: AbstractRecordsStateService,
            useClass: PortalStoreRecordsStateService
        },
        {
            provide: MHSearchRoutingService,
            useFactory: () => new MHSearchRoutingService('/published')
        },
        {
            provide: ShSearchRoutingService,
            useExisting: MHSearchRoutingService
        },
        {
            provide: ZoneRestrictions,
            useClass: PortalZoneRestrictions
        },
        {
            provide: ShProfileConfigService,
            useClass: MhProfileConfigService
        },

        ShDynamicDialogService,
        {
            provide: ShAbstractToastService,
            useExisting: BootstrapSandboxService // Any subclass of BaseSandbox will do
        },
        {
            provide: ShAbstractRecordDownloadService,
            useClass: ShRecordDownloadService
        },
        {
            provide: ShRecordService,
            useClass: SearchService
        },
        {
            provide: ConfigService,
            useExisting: ApplicationConfigService
        },
        { provide: AbstractGeoSandboxService, useClass: GeoSandboxService },
        { provide: PreviewDialogActionsService, useClass: PortalPreviewDialogActionsService },
        {
            provide: BaseAdvancedSearchSandbox,
            useClass: PublicAdvancedSearchSandboxService
        },
        // #endregion
        // #region Interceptors
        { provide: HTTP_INTERCEPTORS, useClass: RefreshTokenInterceptor, multi: true },
        { provide: 'REFRESH_STRATEGIES', useClass: RefreshShareTokenStrategy, multi: true },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: PortalApiInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: TokenInterceptor,
            multi: true
        },
        {
            provide: BaseSandbox,
            useExisting: PublishedSandbox
        },
        {
            provide: CACHE_CONFIG,
            useValue: [
                { url: /profiles(\/|\?)/ },
                { url: /organisations(?:\/|\?|$)/ },
                { url: /classifications(?:\/|\?|$)/ },
                { url: /shares(?:\/|\?|$)/ },
                { url: /export-locations\/default(?:\/|\?|$)/ }
            ]
        },
        {
            provide: PAGE_TITLE_CONFIG,
            useValue: {
                appTitle: 'Portal'
            }
        }
        // #endregion
    ],
    bootstrap: [RootContainerComponent]
})
export class BootstrapModule {}
